import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Instagram,
  Twitter,
  Youtube,
  Globe,
  Mail,
  MessageSquare,
  Coffee
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import '../../styles/linktree.css';

interface LinkItem {
  id: string;
  label: string;
  url: string;
  isActive: boolean;
  order: number;
}

interface SocialLinks {
  instagram?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  website?: string;
  email?: string;
  whatsapp?: string;
}

interface LinktreeData {
  title: string;
  description: string;
  logoUrl: string;
  theme: 'coffee' | 'dark' | 'glassmorphism' | 'sunset' | 'custom';
  customTheme?: {
    backgroundColor: string;
    buttonColor: string;
    buttonTextColor: string;
    textColor: string;
  };
  links: LinkItem[];
  socialLinks: SocialLinks;
}

const DEFAULT_DATA: LinktreeData = {
  title: 'SobCof',
  description: 'Kahve, Arkadaşlık ve Eğlence! ☕️',
  logoUrl: '',
  theme: 'coffee',
  links: [
    {
      id: '1',
      label: 'App Store\'dan İndir 🍎',
      url: 'https://apps.apple.com/tr/app/sobcof/id6751366978?l=tr',
      isActive: true,
      order: 1
    },
    {
      id: '2',
      label: 'Google Play\'den İndir 🤖',
      url: 'https://play.google.com/store/apps/details?id=com.sobcof.sobcofandroid',
      isActive: true,
      order: 2
    }
  ],
  socialLinks: {
    instagram: 'sobcof',
    website: 'https://sobcof.com',
    email: 'sobcof@gmail.com'
  }
};

const getCachedData = (): LinktreeData => {
  try {
    const cached = localStorage.getItem('sobcof_linktree_data');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (err) {
    console.error('Error parsing cached linktree data:', err);
  }
  return DEFAULT_DATA;
};

const LinktreePage: React.FC = () => {
  const [data, setData] = useState<LinktreeData>(getCachedData);

  useEffect(() => {
    const fetchLinktreeData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/public/links`);
        if (response.data.success) {
          const freshData = response.data.data;
          setData(freshData);
          localStorage.setItem('sobcof_linktree_data', JSON.stringify(freshData));
        }
      } catch (err) {
        console.error('Error fetching public Linktree settings:', err);
      }
    };

    const recordPageView = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/public/links/view`);
      } catch (err) {
        console.error('Error recording page view:', err);
      }
    };

    fetchLinktreeData();
    recordPageView();
  }, []);

  const handleLinkClick = async (type: 'link' | 'social', id: string) => {
    try {
      await axios.post(`${API_BASE_URL}/api/public/links/click`, { type, id });
    } catch (err) {
      console.error('Error recording link click:', err);
    }
  };

  const getLinkIcon = (url: string, label: string) => {
    const lowercaseUrl = (url || '').toLowerCase();
    const lowercaseLabel = (label || '').toLowerCase();

    // App Store / Apple
    if (
      lowercaseUrl.includes('apple.com') ||
      lowercaseUrl.includes('apps.apple') ||
      lowercaseLabel.includes('app store') ||
      lowercaseLabel.includes('apple') ||
      lowercaseLabel.includes('ios')
    ) {
      return (
        <svg viewBox="0 0 384 512" fill="currentColor" style={{ width: '18px', height: '18px', marginRight: '10px', flexShrink: 0 }}>
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-48-20.1-77.5-19.6-38.6.6-74.1 22.4-93.8 56.4-39.9 69-10.2 172.7 28.5 228.5 18.9 27.2 41.2 57.6 70.7 56.5 28.1-1.1 38.8-18.1 70.8-18.1 32 0 41.8 18.1 70.8 17.6 30-1.1 49.6-27.7 68.3-55 21.7-31.7 30.6-62.5 31-64.2-1-.4-59.5-22.8-60.1-88zm-52.7-185.3c15.2-18.2 25.5-43.6 22.7-68.9-21.7 1-48.4 14.6-63.9 32.7-13.8 15.8-25.8 41.5-22.5 66.2 24.2 1.9 48.9-11.8 63.7-30z"/>
        </svg>
      );
    }

    // Google Play / Android
    if (
      lowercaseUrl.includes('play.google.com') ||
      lowercaseLabel.includes('google play') ||
      lowercaseLabel.includes('play store') ||
      lowercaseLabel.includes('android')
    ) {
      return (
        <svg viewBox="0 0 512 512" fill="currentColor" style={{ width: '18px', height: '18px', marginRight: '10px', flexShrink: 0 }}>
          <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58 33.3-60.1-60.1L472.5 220c8.2-4.7 8.2-16.7-.3-21.4zM325.3 277.7l60.1 60.1L104.6 499l220.7-221.3z"/>
        </svg>
      );
    }

    // WhatsApp
    if (lowercaseUrl.includes('wa.me') || lowercaseUrl.includes('whatsapp') || lowercaseLabel.includes('whatsapp')) {
      return <MessageSquare size={18} style={{ marginRight: '10px', flexShrink: 0 }} />;
    }

    // Instagram
    if (lowercaseUrl.includes('instagram.com') || lowercaseLabel.includes('instagram') || lowercaseLabel.includes('insta')) {
      return <Instagram size={18} style={{ marginRight: '10px', flexShrink: 0 }} />;
    }

    // TikTok
    if (lowercaseUrl.includes('tiktok.com') || lowercaseLabel.includes('tiktok')) {
      return (
        <svg viewBox="0 0 448 512" fill="currentColor" style={{ width: '18px', height: '18px', marginRight: '10px', flexShrink: 0 }}>
          <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.52V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/>
        </svg>
      );
    }

    // Website / Globe
    if (lowercaseLabel.includes('web') || lowercaseLabel.includes('site') || lowercaseUrl.includes('http')) {
      return <Globe size={18} style={{ marginRight: '10px', flexShrink: 0 }} />;
    }

    // Default Cafe / Coffee Cup
    return <Coffee size={18} style={{ marginRight: '10px', flexShrink: 0 }} />;
  };

  // Fallback default state if API errors
  const finalData = data;

  const activeLinks = finalData.links
    .filter(link => link.isActive)
    .sort((a, b) => a.order - b.order);

  // Social Links Builder helper
  const renderSocialIcon = (key: string, value: string) => {
    if (!value || value.trim() === '') return null;

    let href = '';
    let icon = null;

    switch (key) {
      case 'instagram':
        href = `https://instagram.com/${value.replace('@', '')}`;
        icon = <Instagram size={20} />;
        break;
      case 'twitter':
        href = `https://twitter.com/${value.replace('@', '')}`;
        icon = <Twitter size={20} />;
        break;
      case 'youtube':
        href = value.startsWith('http') ? value : `https://youtube.com/${value}`;
        icon = <Youtube size={20} />;
        break;
      case 'website':
        href = value.startsWith('http') ? value : `https://${value}`;
        icon = <Globe size={20} />;
        break;
      case 'email':
        href = `mailto:${value}`;
        icon = <Mail size={20} />;
        break;
      case 'whatsapp':
        // Clean whatsapp number
        const cleanPhone = value.replace(/[^0-9]/g, '');
        href = `https://wa.me/${cleanPhone}`;
        icon = <MessageSquare size={20} />;
        break;
      case 'tiktok':
        href = `https://tiktok.com/@${value.replace('@', '')}`;
        icon = (
          <svg viewBox="0 0 448 512" fill="currentColor" style={{ width: '18px', height: '18px' }}>
            <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.52V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/>
          </svg>
        );
        break;
      default:
        return null;
    }

    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="linktree-social-icon"
        title={key.toUpperCase()}
        onClick={() => handleLinkClick('social', key)}
      >
        {icon}
      </a>
    );
  };

  // Custom Theme CSS Variables
  const containerStyle = finalData.theme === 'custom' && finalData.customTheme
    ? ({
        '--custom-bg': finalData.customTheme.backgroundColor,
        '--custom-text': finalData.customTheme.textColor,
        '--custom-btn-bg': finalData.customTheme.buttonColor,
        '--custom-btn-text': finalData.customTheme.buttonTextColor,
      } as React.CSSProperties)
    : {};

  // Entrance animations using framer-motion
  const listVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        damping: 15,
        stiffness: 110
      }
    }
  };

  return (
    <div
      className={`linktree-container theme-${finalData.theme}`}
      style={containerStyle}
    >
      <motion.div
        className="linktree-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Avatar/Logo */}
        <motion.div
          className="linktree-avatar-wrapper"
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', damping: 12, delay: 0.2 }}
        >
          {finalData.logoUrl ? (
            <img
              src={finalData.logoUrl}
              alt={finalData.title}
              className="linktree-avatar"
            />
          ) : (
            <img
              src="/assets/LogoWhite.png"
              alt={finalData.title}
              className="linktree-avatar"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', padding: '12px', boxSizing: 'border-box' }}
            />
          )}
        </motion.div>

        {/* Title */}
        <motion.div
          className="linktree-title-container"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '8px' }}
        >
          {finalData.title && (
            finalData.title.endsWith('.png') ||
            finalData.title.endsWith('.jpg') ||
            finalData.title.endsWith('.jpeg') ||
            finalData.title.endsWith('.svg') ||
            finalData.title.startsWith('/assets/') ||
            finalData.title.startsWith('http')
          ) ? (
            <img
              src={finalData.title}
              alt="Profile Title Logo"
              style={{ maxHeight: '44px', width: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <h1 className="linktree-title" style={{ margin: 0 }}>
              {finalData.title || 'SobCof'}
            </h1>
          )}
        </motion.div>

        {/* Description */}
        <motion.p
          className="linktree-description"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {finalData.description}
        </motion.p>

        {/* Links List */}
        <motion.div
          className="linktree-links"
          variants={listVariants}
          initial="hidden"
          animate="show"
        >
          {activeLinks.length > 0 ? (
            activeLinks.map((link) => (
              <motion.a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="linktree-link-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleLinkClick('link', link.id)}
              >
                {getLinkIcon(link.url, link.label)}
                <span>{link.label}</span>
              </motion.a>
            ))
          ) : (
            <motion.p
              style={{ fontSize: '14px', opacity: 0.6, fontStyle: 'italic', margin: '20px 0' }}
              variants={itemVariants}
            >
              Henüz eklenmiş bağlantı bulunmamaktadır.
            </motion.p>
          )}
        </motion.div>

        {/* Social Icons Footer */}
        <motion.div
          className="linktree-socials"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {Object.entries(finalData.socialLinks).map(([key, value]) =>
            renderSocialIcon(key, value as string)
          )}
        </motion.div>

        {/* Footer Brand */}
        <motion.div
          className="linktree-footer-brand"
          style={{ marginTop: '30px', fontSize: '11px', opacity: 0.4, letterSpacing: '0.5px' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 0.8 }}
        >
          Powered by SobCof
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LinktreePage;
